import React from 'react';
import { Link, useLoaderData, useLocation, useNavigate, useOutlet } from 'react-router-dom';

import {
  MagnifyingGlassIcon,
  BellIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { PageTitle } from '@oysterjs/uiv2/page';
import { Badge } from '@oysterjs/uiv2/badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/uiv2/fieldset';
import { Select } from '@oysterjs/uiv2/select';
import { Await } from '@oysterjs/uiv2/async';
import {
  CaraWorkspaceState,
  EmailFieldsFragment,
  EmailSenderRole,
  EmailThreadConversationFieldsFragment,
  EmailThreadFieldsFragment,
  EmailThreadState,
  ServiceRequestState
} from '../api/graphql';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { AnimatePresence } from 'motion/react';
import { MountableSlideout } from '@oysterjs/uiv2/slideout';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import Markdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const EmailThreadsPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as
    | { data: Promise<{ emailThreads?: EmailThreadFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Emails" description="Email conversations that Cara has been a part of">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full">
              <Fieldset>
                <FieldGroup>
                  <div className="flex gap-4">
                    <Field className="w-full max-w-md">
                      <Label className="sr-only" data-slot="none">
                        Filter table
                      </Label>
                      <InputGroup>
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <Input
                          type="search"
                          name="search"
                          placeholder="Start typing to filter..."
                        />
                      </InputGroup>
                    </Field>
                    <Field>
                      <Label className="sr-only" data-slot="none">
                        Filter by status
                      </Label>
                      <Select name="status" value="">
                        <option value="" disabled>
                          Filter by status
                        </option>
                        <option value="Pending">In Progress</option>
                        <option value="Assigned to AI">Completed</option>
                        <option value="Ready for Review">Error</option>
                      </Select>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </form>
          </div>
        </div>

        <React.Suspense fallback={<EmailThreadsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ emailThreads }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white shadow">
                {!emailThreads?.length && (
                  <div className="text-center py-12">
                    <EnvelopeIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                      No email threads found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      Cara hasn't been a part of any email conversations yet.
                    </p>
                  </div>
                )}
                {!!emailThreads?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Customer</TableHeader>
                        <TableHeader className="py-4">Subject</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Waiting For</TableHeader>
                        <TableHeader className="py-4">Last Update</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailThreads.map((emailThread) => (
                        <TableRow key={emailThread.id} href={`/app/emails/${emailThread.id}`}>
                          <TableCell>
                            {emailThread.account?.account.name && (
                              <div className="font-medium flex gap-1 items-center">
                                <div>{emailThread.account.account.name} </div>
                                <div>
                                  <CheckBadgeIcon className="size-4 text-blue-400" />
                                </div>
                              </div>
                            )}
                            {!emailThread.account?.account.name && !!emailThread.fromName && (
                              <div className="font-medium">{emailThread.fromName}</div>
                            )}
                            {!emailThread.account?.account.name && !emailThread.fromName && (
                              <div className="font-medium text-neutral-500">—</div>
                            )}
                            {emailThread.fromEmail && (
                              <div className="text-sm text-neutral-500">
                                {emailThread.fromEmail}
                              </div>
                            )}
                            {!emailThread.fromEmail && (
                              <div className="text-sm text-neutral-500">—</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {emailThread.subject && <div>{emailThread.subject}</div>}
                            {!emailThread.subject && <div className="text-neutral-500">—</div>}
                          </TableCell>
                          <TableCell>
                            <EmailThreadStateBadge state={emailThread.state} />
                          </TableCell>
                          <TableCell>
                            {emailThread.waitingFor && (
                              <EmailThreadParticipantBadge role={emailThread.waitingFor} />
                            )}
                            {!emailThread.waitingFor && <div className="text-neutral-500">—</div>}
                          </TableCell>
                          <TableCell>
                            {new Intl.DateTimeFormat('en-US', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            }).format(new Date(emailThread.updatedAt))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </PageTitle>
  );
};

const EmailThreadsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading inbox...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

const EmailThreadStateBadge = (props: { state: EmailThreadState }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [EmailThreadState.Active]: 'blue',
          [EmailThreadState.Ended]: 'green'
        }[props.state] || 'neutral'
      }
    >
      {titleCase(props.state)}
    </Badge>
  );
};

const EmailThreadParticipantBadge = (props: { role: EmailSenderRole }) => {
  return (
    <Badge
      // eslint-disable-next-line
  // @ts-ignore
      color={
        {
          [EmailSenderRole.Client]: 'blue',
          [EmailSenderRole.Participant]: 'blue',
          [EmailSenderRole.Agent]: 'amber',
          [EmailSenderRole.Cara]: 'green'
        }[props.role] || 'neutral'
      }
    >
      {titleCase(props.role === EmailSenderRole.Participant ? EmailSenderRole.Client : props.role)}
    </Badge>
  );
};

export const EmailThreadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
        }>;
      }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/emails');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Email Thread">
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) => data?.emailThread && <EmailThread emailThread={data.emailThread} />}
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const EmailThread = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <dl>
      <EmailThreadProperties emailThread={props.emailThread} />
      <EmailThreadAccountSummary emailThread={props.emailThread} />
      {props.emailThread.summary && <EmailThreadSummary emailThread={props.emailThread} />}
      {props.emailThread.workspace && <EmailThreadWorkspace emailThread={props.emailThread} />}
      {props.emailThread.serviceRequests.length > 0 && (
        <EmailThreadServiceRequests emailThread={props.emailThread} />
      )}
      {(props.emailThread?.participantEmails?.length || 0) > 0 && (
        <EmailThreadConversation emailThread={props.emailThread} />
      )}
    </dl>
  );
};

const EmailThreadProperties = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  const properties = [
    {
      title: 'Subject',
      value: props.emailThread.subject
    },
    {
      title: 'Status',
      value: <EmailThreadStateBadge state={props.emailThread.state} />
    },
    {
      title: 'Waiting For',
      value: props.emailThread.waitingFor ? (
        <EmailThreadParticipantBadge role={props.emailThread.waitingFor} />
      ) : (
        <div className="text-neutral-500">—</div>
      )
    },
    {
      title: 'Started At',
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(props.emailThread.createdAt))
    },
    {
      title: 'Last Update',
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(props.emailThread.updatedAt))
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
      {properties.map((property) => (
        <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm/6 font-medium text-neutral-900">{property.title}</dt>
          <dd className="text-sm/6 text-neutral-700 sm:col-span-2">{property.value}</dd>
        </div>
      ))}
    </div>
  );
};

const EmailThreadAccountSummary = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200">
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900">Client</dt>
        <dd className="text-sm/6 text-neutral-700 sm:col-span-2">
          {props.emailThread.fromName || <div className="text-neutral-500">—</div>}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900">Account</dt>
        <dd className="text-sm/6 text-neutral-700 sm:col-span-2">
          {props.emailThread.account?.account.name && (
            <Link to={`/app/accounts/${props.emailThread.account.id}`} className="hover:underline">
              <div className="flex items-center gap-1">
                {props.emailThread.account.account.name}
                <ArrowTopRightOnSquareIcon className="size-4" />
              </div>
            </Link>
          )}
          {!props.emailThread.account?.account.name && <div className="text-neutral-500">—</div>}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900">Email Address</dt>
        <dd className="text-sm/6 text-neutral-700 sm:col-span-2">
          {props.emailThread.fromEmail || <div className="text-neutral-500">—</div>}
        </dd>
      </div>
    </div>
  );
};

const EmailThreadSummary = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Summary</dt>
        <dd className="text-sm/6 text-neutral-700">{props.emailThread.summary}</dd>
      </div>
    </div>
  );
};

const ServiceRequestBadge = (props: { state: ServiceRequestState }) => {
  return (
    <Badge
      // eslint-disable-next-line
    // @ts-ignore
      color={
        {
          [ServiceRequestState.Open]: 'amber',
          [ServiceRequestState.Closed]: 'green'
        }[props.state] || 'neutral'
      }
    >
      {props.state}
    </Badge>
  );
};

const EmailThreadServiceRequests = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200">
      <div className="flex flex-col sm:gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Service Requests</dt>
        <dd className="text-sm/6 text-neutral-900">
          <ul
            role="list"
            className="divide-y divide-neutral-100 rounded-md border border-neutral-200"
          >
            {props.emailThread.serviceRequests.map((serviceRequest) => (
              <li key={serviceRequest.id} className="flex justify-between py-4 pl-4 pr-5 text-sm/6">
                <div className="flex w-0 flex-1">
                  <BellIcon aria-hidden="true" className="size-6 shrink-0 text-neutral-400" />
                  <div className="ml-4 min-w-0">
                    <Link
                      to={`/app/servicing/${serviceRequest.id}/overview`}
                      className="truncate font-medium hover:underline"
                    >
                      {titleCase(serviceRequest.type)}
                    </Link>
                    <div className="text-neutral-500">
                      {serviceRequest.details ||
                        serviceRequest.question?.question ||
                        serviceRequest.cancelPolicy?.reason}
                    </div>
                  </div>
                </div>
                <div className="ml-4 shrink-0">
                  <ServiceRequestBadge state={serviceRequest.state} />
                </div>
              </li>
            ))}
          </ul>
        </dd>
      </div>
    </div>
  );
};

const WorkspaceBadge = (props: { state: CaraWorkspaceState }) => {
  return (
    <Badge
      // eslint-disable-next-line
    // @ts-ignore
      color={
        {
          [CaraWorkspaceState.Created]: 'neutral',
          [CaraWorkspaceState.Idle]: 'green',
          [CaraWorkspaceState.Working]: 'blue',
          [CaraWorkspaceState.WaitingForInput]: 'amber'
        }[props.state] || 'neutral'
      }
    >
      {props.state}
    </Badge>
  );
};

const EmailThreadWorkspace = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200">
      <div className="flex flex-col sm:gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Workspace</dt>
        <dd className="text-sm/6 text-neutral-900">
          <ul
            role="list"
            className="divide-y divide-neutral-100 rounded-md border border-neutral-200"
          >
            <li className="flex justify-between py-4 pl-4 pr-5 text-sm/6">
              <div className="flex w-0 flex-1">
                <BellIcon aria-hidden="true" className="size-6 shrink-0 text-neutral-400" />
                <div className="ml-4 min-w-0">
                  <Link
                    to={`/app/workspaces/${props.emailThread.workspace?.id}`}
                    className="truncate font-medium hover:underline"
                  >
                    Go to Workspace
                  </Link>
                  <div className="text-neutral-500">{props.emailThread.workspace?.title}</div>
                </div>
              </div>
              <div className="ml-4 shrink-0">
                <WorkspaceBadge
                  state={props.emailThread.workspace?.state || CaraWorkspaceState.Created}
                />
              </div>
            </li>
          </ul>
        </dd>
      </div>
    </div>
  );
};

const EmailThreadConversation = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900">Transcript</dt>
        <dd className="text-sm/6 text-neutral-700 flex flex-col gap-2.5">
          {props.emailThread.participantEmails.map((message) => {
            if (
              [EmailSenderRole.Client, EmailSenderRole.Participant].includes(message.senderRole)
            ) {
              return <UserMessage message={message} />;
            }
            if (message.senderRole === EmailSenderRole.Agent) {
              return <AgentMessage message={message} />;
            }
            if (message.senderRole === EmailSenderRole.Cara) {
              return <AIMessage message={message} />;
            }
          })}
        </dd>
      </div>
    </div>
  );
};

const UserMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-neutral-100 p-4 rounded-lg">
      <div className="uppercase text-xs text-neutral-500 font-medium">
        {props.message.from.name}
      </div>
      <pre className="text-sm/6 font-body font-normal whitespace-pre-wrap text-neutral-700 mt-2">
        {props.message.body}
      </pre>
    </div>
  );
};

const AgentMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-neutral-100 p-4 rounded-lg">
      <div className="uppercase text-xs text-neutral-500 font-medium">
        {props.message.from.name}
      </div>
      <pre className="text-sm/6 font-body font-normal whitespace-pre-wrap text-neutral-700 mt-2">
        {props.message.body}
      </pre>
    </div>
  );
};

const AIMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-blue-50 p-4 rounded-lg">
      <div className="uppercase text-xs text-blue-500 font-medium">{props.message.from.name}</div>
      <div className="mt-2 text-neutral-700 email-markdown-container">
        <Markdown
          remarkPlugins={[[remarkGfm, {}]]}
          rehypePlugins={[
            [rehypeRaw, { tagFilter: true }],
            [rehypeSanitize, {}]
          ]}
        >
          {props.message.body}
        </Markdown>
      </div>
    </div>
  );
};
